import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { gurukulService } from '../../services';
import { formatCurrency } from '../../utils/formatters';
import PageHeader from '../../components/common/PageHeader';
import { useNavigate } from 'react-router-dom';

const GurukulManagement = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [approvingId, setApprovingId] = useState(null);

  // Pagination & Selection
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showModal] = useState(false); // Modal logic was moved to separate pages

  const limit = 10;

  useEffect(() => {
    fetchCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        limit,
        offset: (currentPage - 1) * limit,
        sortBy: 'id',
        order: 'DESC',
      };

      const response = await gurukulService.getAdminGurukuls(params);
      const candidatesList = response?.data?.items || response?.items || [];
      const total = response?.data?.totalCount || response?.totalCount || 0;

      setCandidates(candidatesList);
      setTotalCount(total);
    } catch (err) {
      console.error('Error fetching candidates:', err);
      setError(t('admin.gurukul.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (id, currentStatus) => {
    setError('');
    setSuccess('');

    try {
      const candidate = candidates.find(c => c.id === id);
      if (!candidate) return;

      await gurukulService.updateGurukul(id, {
        ...candidate,
        isEnabled: !currentStatus,
      });

      // Update Gurukul in list
      setCandidates(candidates.map(c => c.id === id ? { ...c, isEnabled: !currentStatus } : c));

      if (selectedCandidate && selectedCandidate.id === id) {
        setSelectedCandidate({ ...selectedCandidate, isEnabled: !currentStatus });
      }

      setSuccess(`${t('admin.gurukul.table.name')} ${!currentStatus ? t('admin.gurukul.status.enabled') : t('admin.gurukul.status.disabled')} ${t('common.success')}!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error toggling Gurukul status:', err);
      setError(err.response?.data?.message || t('admin.gurukul.messages.statusUpdateError'));
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleEdit = (candidate) => {
    navigate(`/admin/gurukuls/${candidate.id}/edit`);
  };

  const isPendingApproval = (candidate) =>
    candidate.registrationStatus === 'PENDING' ||
    candidate.approvalStatus === 'PENDING' ||
    candidate.accountStatus === 'PENDING_APPROVAL';

  const handleApproveRegistration = async (candidate) => {
    setError('');
    setSuccess('');
    setApprovingId(candidate.id);
    try {
      const resp = await gurukulService.approveGurukulRegistration(candidate.id);
      const updated = resp?.data || resp;

      // Optimistically update UI so admin doesn't need manual refresh
      setCandidates((prev) =>
        prev.map((c) => {
          if (c.id !== candidate.id) return c;
          return {
            ...c,
            ...(updated && typeof updated === 'object' ? updated : null),
            registrationStatus: 'APPROVED',
            approvalStatus: 'APPROVED',
            accountStatus: 'ACTIVE',
          };
        })
      );
      setSuccess(t('admin.gurukul.messages.approveSuccess'));
      // Re-sync with backend (don’t block UI)
      fetchCandidates();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error('Error approving Gurukul registration:', err);
      setError(err.response?.data?.message || t('admin.gurukul.messages.approveError'));
      setTimeout(() => setError(''), 5000);
    } finally {
      setApprovingId(null);
    }
  };

  const handleDelete = async () => {
    if (!candidateToDelete) return;

    setError('');
    setSuccess('');

    try {
      // Delete from Local (Source of Truth handles sync)
      await gurukulService.deleteGurukul(candidateToDelete.id);

      // Remove from list
      setCandidates(candidates.filter(c => c.id !== candidateToDelete.id));

      setSuccess(t('admin.gurukul.messages.deleteSuccess'));
      setShowDeleteConfirm(false);
      setCandidateToDelete(null);

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting Gurukul:', err);
      setError(err.response?.data?.message || t('admin.gurukul.messages.deleteError'));
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDownloadSample = async () => {
    try {
      const blob = await gurukulService.downloadSample();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'gurukul_import_sample.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSuccess(t('admin.gurukul.messages.sampleDownloadSuccess'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error downloading sample:', err);
      setError(t('admin.gurukul.messages.sampleDownloadError'));
      setTimeout(() => setError(''), 3000);
    }
  };

  const executeImport = async () => {
    setImporting(true);
    setError('');
    setSuccess('');

    try {
      await gurukulService.importGurukuls(importFile);
      setSuccess(t('admin.gurukul.messages.importSuccess'));
      setShowImportModal(false);
      setImportFile(null);
      setImporting(false);

      // Force refresh
      setCandidates([]); // Clear list to show loading
      setLoading(true);
      setTimeout(() => {
        fetchCandidates();
      }, 500);

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setImporting(false);
      console.error('Error importing Gurukuls:', err);
      setError(err.response?.data?.message || t('admin.gurukul.messages.importError'));
    }
  };

  const totalPages = Math.ceil(totalCount / limit);

  if (loading && candidates.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50 pb-8">
      <PageHeader
        title={t('admin.gurukul.title')}
        subtitle={t('admin.gurukul.subtitle')}
        icon={
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        }
      />
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-8 flex justify-end items-start">
          <div className="flex gap-3">
            <button
              onClick={handleDownloadSample}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {t('admin.gurukul.buttons.sampleXlsx')}
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              {t('admin.gurukul.buttons.importXlsx')}
            </button>
            <button
              onClick={() => navigate('/admin/gurukuls/new')}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('admin.gurukul.buttons.addGurukul')}
            </button>
          </div>
        </div>

        {error && !showModal && !showImportModal && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {success && !showModal && !showImportModal && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
            {success}
          </div>
        )}

        {/* Gurukuls Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-100 border-b-2 border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.gurukul.table.name')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.gurukul.table.address')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.gurukul.table.district')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.gurukul.table.province')}
                  </th>

                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.gurukul.table.raised')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.gurukul.table.status')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.gurukul.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {candidates.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      {t('admin.gurukul.table.noGurukuls')}
                    </td>
                  </tr>
                ) : (
                  candidates.map((candidate) => (
                    <tr key={candidate.id} className="hover:bg-primary-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0">
                            {candidate.photoKeyUrl ? (
                              <img
                                className="h-10 w-10 rounded-full object-cover"
                                src={candidate.photoKeyUrl}
                                alt={candidate.name}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div
                              className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-semibold"
                              style={{ display: candidate.photoKeyUrl ? 'none' : 'flex' }}
                            >
                              {candidate.name.charAt(0)}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{candidate.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{candidate.nagarpalikaGaupalika}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{candidate.district}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{candidate.province}</div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatCurrency(candidate.raisedAmount || 0, lang)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isPendingApproval(candidate) ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-900">
                            {t('admin.gurukul.status.pendingApproval')}
                          </span>
                        ) : (
                          <button
                            onClick={() => handleToggleEnabled(candidate.id, candidate.isEnabled)}
                            className={`px-2 py-1 text-xs font-semibold rounded-full ${candidate.isEnabled
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-red-100 text-red-800 hover:bg-red-200'
                              } transition`}
                          >
                            {candidate.isEnabled ? t('admin.gurukul.status.enabled') : t('admin.gurukul.status.disabled')}
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2 flex-wrap">
                          {isPendingApproval(candidate) && (
                            <button
                              type="button"
                              disabled={approvingId === candidate.id}
                              onClick={() => handleApproveRegistration(candidate)}
                              className="text-green-600 hover:text-green-800 font-medium disabled:opacity-50"
                            >
                              {approvingId === candidate.id ? t('common.loading') : t('admin.gurukul.buttons.approveRegistration')}
                            </button>
                          )}
                          <button
                            onClick={() => handleEdit(candidate)}
                            className="text-primary-600 hover:text-primary-900 transition"
                          >
                            {t('admin.gurukul.buttons.edit')}
                          </button>
                          <button
                            onClick={() => {
                              setCandidateToDelete(candidate);
                              setShowDeleteConfirm(true);
                            }}
                            className="text-red-600 hover:text-red-900 transition"
                          >
                            {t('admin.gurukul.buttons.delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-primary-50 disabled:opacity-50"
                >
                  {t('common.previous')}
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-primary-50 disabled:opacity-50"
                >
                  {t('common.next')}
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    {t('common.pagination.showing')} <span className="font-medium">{(currentPage - 1) * limit + 1}</span> {t('common.pagination.to')}{' '}
                    <span className="font-medium">{Math.min(currentPage * limit, totalCount)}</span> {t('common.pagination.of')}{' '}
                    <span className="font-medium">{totalCount}</span> {t('common.pagination.results')}
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {t('actions.previous')}
                    </button>
                    {[...Array(totalPages)].map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentPage(idx + 1)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === idx + 1
                          ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                      >
                        {idx + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {t('actions.next')}
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">{t('admin.gurukul.modals.import.title')}</h3>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                  setError('');
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              <p className="mb-2">📋 {t('admin.gurukul.modals.import.help1')}</p>
              <p>{t('admin.gurukul.modals.import.help2')}</p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('admin.gurukul.modals.import.selectFile')}
              </label>
              <input
                type="file"
                accept=".xlsx"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {importFile && (
                <p className="mt-1 text-xs text-green-600">✓ {importFile.name}</p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                  setError('');
                }}
                className="flex-1 px-4 py-2 border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-50 transition-colors"
              >
                {t('actions.cancel')}
              </button>
              <button
                onClick={executeImport}
                disabled={!importFile || importing}
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importing ? t('admin.gurukul.modals.import.importing') : t('admin.gurukul.modals.import.import')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && candidateToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">{t('admin.gurukul.modals.delete.title')}</h3>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setCandidateToDelete(null);
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 mb-2">
                {t('admin.gurukul.modals.delete.confirm')}
              </p>
              <div className="bg-primary-50/50 p-3 rounded-lg border border-primary-100">
                <p className="font-semibold text-gray-900">{candidateToDelete.name}</p>
                <p className="text-sm text-gray-600">{candidateToDelete.nagarpalikaGaupalika}, {candidateToDelete.district}, {candidateToDelete.province}</p>
              </div>
              <p className="text-sm text-red-600 mt-3">
                ⚠️ {t('admin.gurukul.modals.delete.warning')}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setCandidateToDelete(null);
                }}
                className="flex-1 px-4 py-2 border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-50 transition-colors"
              >
                {t('actions.cancel')}
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
              >
                {t('admin.gurukul.modals.delete.button')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GurukulManagement;
